/* eslint-disable ember/no-runloop */
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismIcon } from '@mylighthouse/prism-ember';
import { PRISM_SPACING_1000 } from '@mylighthouse/prism-foundation';
import dayjs from 'dayjs';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import HotelRatesTooltipsShortTermRentalTooltip from 'lumen/components/hotel/rates/tooltips/short-term-rental-tooltip';
import { getWeekendPlotBands } from 'lumen/utils/charts/chart-utils';
import { GROUP } from 'lumen/utils/constants/chart-color-constants';
import {
  DATE_AGGREGATION,
  DAY_IN_MS,
  HIGHCHARTS_MISSING_MARKER,
  WEEK_IN_MS,
} from 'lumen/utils/constants/general-constants';
import {
  SHORT_TERM_RENTAL_OTB,
  SHORT_TERM_RENTAL_RATE,
} from 'lumen/utils/constants/short-term-rental-constants';

import styles from './chart-short-term-rental-evolution-range.module.css';

interface HotelShortTermRentalChartsChartShortTermRentalEvolutionRangeSignature {
  Args: {
    onUpdateChartReference: unknown;
    refDaysAgo: unknown;
    selectedDateAggregation: unknown;
    sortedData: unknown;
  };
}

export default class HotelShortTermRentalChartsChartShortTermRentalEvolutionRange extends Component<HotelShortTermRentalChartsChartShortTermRentalEvolutionRangeSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare router: Services['router'];

  // @ts-expect-error: Incorrect type
  @tracked eventsData;
  // @ts-expect-error: Incorrect type
  @tracked holidays;
  // @ts-expect-error: Incorrect type
  @tracked leadTime;
  // @ts-expect-error: Incorrect type
  @tracked occupancy;
  // @ts-expect-error: Incorrect type
  @tracked period;
  // @ts-expect-error: Incorrect type
  @tracked rate;
  // @ts-expect-error: Incorrect type
  @tracked rentalOccupancy;
  // @ts-expect-error: Incorrect type
  @tracked rentalRate;

  dataMax = null;
  dataMin = null;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    // @ts-expect-error: Incorrect type
    const chartData = [];
    // @ts-expect-error: Incorrect type
    if (Object.keys(this.args.sortedData).length === 0) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return chartData;
    }

    const rateSeries = {
      name: this.intl.t('short_term_rental.my_rate'),
      data: [],
      type: 'line',
      fillOpacity: 0.15,
      borderWidth: 1,
      marker: { enabled: true, radius: 4 },
      zIndex: 10,
      legendIndex: 3,
    };

    const otbSeries = {
      name: this.intl.t('rates.my_OTB'),
      data: [],
      type: 'column',
      yAxis: 1,
      color: GROUP,
      stack: 'otb',
      zIndex: 1,
      legendIndex: 0,
    };

    const rentalRateSeries = {
      name: this.intl.t('alternative_lodging.rate'),
      data: [],
      type: 'line',
      color: SHORT_TERM_RENTAL_RATE,
      fillOpacity: 0.15,
      borderWidth: 1,
      marker: { enabled: true, radius: 4 },
      zIndex: 10,
      legendIndex: 4,
    };

    const rentalOTBSeries = {
      name: this.intl.t('alternative_lodging.otb'),
      data: [],
      type: 'column',
      yAxis: 1,
      color: SHORT_TERM_RENTAL_OTB,
      borderColor: 'var(--prism-color-border-inverse-emphasis)',
      stack: 'otb',
      zIndex: 1,
      legendIndex: 2,
    };

    const marketOtbSeries = {
      name: this.intl.t('rates.tooltip.market_otb'),
      data: [],
      type: 'column',
      yAxis: 1,
      color: 'var(--prism-color-chart-accent-secondary-figure)',
      stack: 'otb',
      zIndex: 1,
      legendIndex: 1,
    };

    // @ts-expect-error: Incorrect type
    let previousOwnHotelRate = null;
    // @ts-expect-error: Incorrect type
    let previousRentalRate = null;
    // @ts-expect-error: Incorrect type
    Object.keys(this.args.sortedData).forEach((key) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const {
        period,
        occupancy,
        rentalRate,
        rentalOccupancy,
        events,
        holidays,
        // @ts-expect-error: Incorrect type
      } = this.args.sortedData[key];
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const rate = period.rates[this.currentHotel.hotelinfo.id][0];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const x = period.day.date.utc(true).unix() * 1000;
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const leadTime = period.day.date.diff(this.args.fromDate, 'days');

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const isOwnHotelRateMissing = rate.value === 0;
      // @ts-expect-error: Incorrect type
      rateSeries.data.push({
        x,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        y: isOwnHotelRateMissing ? previousOwnHotelRate : rate.value,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        period,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        rate,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        occupancy,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        rentalRate,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        rentalOccupancy,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        eventsData: events,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        holidays,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        leadTime,
        marker: isOwnHotelRateMissing ? HIGHCHARTS_MISSING_MARKER : null,
      });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      previousOwnHotelRate = isOwnHotelRateMissing
        ? // @ts-expect-error: Incorrect type
          previousOwnHotelRate
        : // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          rate.value;

      if (this.access.hasOTB) {
        // @ts-expect-error: Incorrect type
        otbSeries.data.push({
          x,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          y: occupancy.occupancyPercentage || null, // Don't show missing OTB data
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          period,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          rate,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          occupancy,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          rentalRate,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          rentalOccupancy,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          eventsData: events,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          holidays,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          leadTime,
        });
      }

      if (this.access.hasMarketOtb) {
        // @ts-expect-error: Incorrect type
        marketOtbSeries.data.push({
          x,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          y: occupancy.benchmarkValue || null, // Don't show missing OTB data
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          period,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          rate,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          occupancy,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          rentalRate,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          rentalOccupancy,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          eventsData: events,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          holidays,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          leadTime,
        });
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const isRentalRateMissing = rentalRate.value === 0;
      // @ts-expect-error: Incorrect type
      rentalRateSeries.data.push({
        x,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        y: isRentalRateMissing ? previousRentalRate : rentalRate.value,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        period,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        rate,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        occupancy,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        rentalRate,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        rentalOccupancy,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        eventsData: events,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        holidays,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        leadTime,
      });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      previousRentalRate = isRentalRateMissing
        ? // @ts-expect-error: Incorrect type
          previousRentalRate
        : // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          rentalRate.value;

      // @ts-expect-error: Incorrect type
      rentalOTBSeries.data.push({
        x,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        y: rentalOccupancy.value || null, // Don't show missing OTB data
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        period,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        rate,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        occupancy,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        rentalRate,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        rentalOccupancy,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        eventsData: events,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        holidays,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        leadTime,
      });
    });
    chartData.push(rateSeries);
    chartData.push(rentalRateSeries);
    if (this.access.hasOTB) {
      chartData.push(otbSeries);
    }
    if (this.access.hasMarketOtb) {
      chartData.push(marketOtbSeries);
    }
    chartData.push(rentalOTBSeries);

    return chartData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    return {
      chart: {
        type: 'line',
        marginTop: PRISM_SPACING_1000,
      },
      rangeSelector: {
        selected: 1,
      },
      scrollbar: {
        enabled: false,
        liveRedraw: false,
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        enabled: true,
        borderWidth: 0,
        itemStyle: {
          color: 'var(--prism-color-text-neutral-subdued)',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        },
        itemHoverStyle: {
          color: 'var(--prism-color-interactive-text-secondary-hover)',
        },
      },
      plotOptions: {
        column: {
          pointWidth: null,
          maxPointWidth: 30,
        },
        series: {
          connectNulls: true,
          states: {
            inactive: {
              opacity: 1,
            },
          },
          cursor: 'pointer',
          point: {
            events: {
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              mouseOver() {
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                this.series.chart.xAxis[0].addPlotLine({
                  // @ts-expect-error: Incorrect type
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  value: this.x,
                  color:
                    'var(--prism-color-chart-accent-secondary-figure-emphasis)',
                  width: 1,
                  id: 'hover-line',
                });
              },
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              mouseOut() {
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                if (this.series.chart.xAxis[0].plotLinesAndBands) {
                  // @ts-expect-error: Incorrect type
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                  this.series.chart.xAxis[0].removePlotLine('hover-line');
                }
              },
            },
          },
          events: {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            click(event) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
              const day = dayjs(event.point.options.x).format('YYYY-MM-DD');
              that.router.transitionTo({
                queryParams: {
                  fromDate: day,
                  toDate: day,
                },
              });
            },
          },
        },
      },
      xAxis: [
        {
          crosshair: false,
          type: 'datetime',
          // Setting the tickLength to 0 removes the vertical line that appears under each x label
          tickLength: 0,
          tickInterval:
            that.args.selectedDateAggregation === DATE_AGGREGATION.WEEK
              ? WEEK_IN_MS
              : DAY_IN_MS,
          plotBands: this.weekendPlotBands,
          labels: {
            useHTML: true,
            y: 20,
            rotation: 0,
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            formatter() {
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              const date = dayjs.utc(this.value).format('YYYY-MM-DD');
              const eventExists =
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                that.args.sortedData[date]?.holidays.length > 0 ||
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                that.args.sortedData[date]?.events.length > 0;

              if (eventExists) {
                return document.getElementById(
                  'dummy-x-axis-label-for-short-term-rental-evolution',
                )?.innerHTML;
              }

              return null;
            },
          },
        },
        {
          type: 'datetime',
          lineColor: 'var(--prism-color-border-neutral-default)',
          crosshair: true,
          // Links the labels to the events labels above
          linkedTo: 0,
          tickInterval:
            that.args.selectedDateAggregation === DATE_AGGREGATION.WEEK
              ? WEEK_IN_MS
              : DAY_IN_MS * 2,
          labels: {
            y: 25,
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            formatter() {
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              const date = dayjs.utc(this.value);
              if (that.args.selectedDateAggregation === DATE_AGGREGATION.WEEK) {
                return that.intl
                  .t('general.week_of', {
                    date: date.format('ll'),
                  })
                  .toString();
              }
              return date.format('ll');
            },
          },
          margin: 10,
        },
      ],
      yAxis: [
        {
          title: {
            text: this.intl.t('general.rate'),
          },
          labels: {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            formatter() {
              // @ts-expect-error: Incorrect type
              return `${that.currentHotel.currencySymbol} ${this.value}`;
            },
          },
        },
        {
          gridLineWidth: 0,
          opposite: true,
          title: {
            text: this.intl.t('general.occupancy'),
          },
          labels: {
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            formatter() {
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              return that.intl.formatNumber(this.value, {
                style: 'percent',
              });
            },
          },
          max: 1,
          showFirstLabel: false,
        },
      ],
      tooltip: {
        hideDelay: 0.2,
        crosshairs: true,
        padding: 0,
        borderWidth: 0,
        shadow: false,
        distance: 20,
        shared: true,
        outside: true,

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const {
            period,
            rate,
            occupancy,
            rentalRate,
            rentalOccupancy,
            leadTime,
            eventsData,
            holidays,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          } = this.points[0].point;
          begin();
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          that.period = period;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          that.rate = rate;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          that.occupancy = occupancy;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          that.rentalOccupancy = rentalOccupancy;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          that.rentalRate = rentalRate;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          that.leadTime = leadTime;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          that.eventsData = [...new Set(eventsData)];
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          that.holidays = holidays;
          end();
          // @ts-expect-error: Incorrect type
          return document.getElementById(
            'dummy-tooltip-for-short-term-rental-evolution',
          ).innerHTML;
        },
        useHTML: true,
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentHotel() {
    return this.hotelAndMarketService.hotel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get weekendPlotBands() {
    // @ts-expect-error: Incorrect type
    const sortedDataArray = Object.keys(this.args.sortedData);
    const startDate = this.hotelAndMarketService.toLocalTime(
      // @ts-expect-error: Incorrect type
      dayjs(sortedDataArray[0]),
    );
    const endDate = this.hotelAndMarketService.toLocalTime(
      // @ts-expect-error: Incorrect type
      dayjs(sortedDataArray[sortedDataArray.length - 1]),
    );

    return getWeekendPlotBands(this.hotelAndMarketService, startDate, endDate);
  }

  <template>
    <GeneralChartsOtaChart
      {{! @glint-expect-error: Incorrect type }}
      @chartOptions={{this.chartOptions}}
      @model={{this.chartData}}
      {{! @glint-expect-error: Incorrect type }}
      @onUpdateChartReference={{@onUpdateChartReference}}
    />

    <div class="hidden" id="dummy-x-axis-label-for-short-term-rental-evolution">
      <PrismIcon @name="calendar-accepted" @size="small" />
    </div>

    <div class="hidden" id="dummy-tooltip-for-short-term-rental-evolution">
      <div class={{styles.tooltip-short-term-rental}}>
        {{#if this.period}}
          <HotelRatesTooltipsShortTermRentalTooltip
            @events={{this.eventsData}}
            @hasShortTermRentalAlert={{false}}
            @holidays={{this.holidays}}
            @leadTime={{this.leadTime}}
            @occupancy={{this.occupancy}}
            @period={{this.period}}
            {{! @glint-expect-error: Incorrect type }}
            @refDaysAgo={{@refDaysAgo}}
            @rentalOccupancy={{this.rentalOccupancy}}
            @rentalRate={{this.rentalRate}}
            @showChanges={{true}}
            @showEventsAndHolidays={{true}}
          />
        {{/if}}
      </div>
    </div>
  </template>
}
